import { c as _c } from "react/compiler-runtime";
import { Box, BoxProps, chakra, Separator } from '@chakra-ui/react';
import { presets, typographyStyles } from '@src/components/sco/MarkdownRenderer/typographyStyles';
import { Wrapper } from '@src/components/sco/RichMarkdownInput/components/EditorWrapper';
import { Element } from '@src/components/sco/RichMarkdownInput/components/Element';
import { Leaf } from '@src/components/sco/RichMarkdownInput/components/Leaf';
import { LinkModal } from '@src/components/sco/RichMarkdownInput/components/LinkModal';
import { Toolbar } from '@src/components/sco/RichMarkdownInput/components/Toolbar';
import { LinkModalProvider, useLinkModal } from '@src/components/sco/RichMarkdownInput/context/LinkModalContext';
import { withEmojis } from '@src/components/sco/RichMarkdownInput/plugins/emojis';
import { withGiphy } from '@src/components/sco/RichMarkdownInput/plugins/giphy';
import { withImages } from '@src/components/sco/RichMarkdownInput/plugins/images';
import { withLinks } from '@src/components/sco/RichMarkdownInput/plugins/links';
import { withLists } from '@src/components/sco/RichMarkdownInput/plugins/lists';
import { withMarkHotKeys } from '@src/components/sco/RichMarkdownInput/plugins/markHotKeys';
import { withMarkdownDetection } from '@src/components/sco/RichMarkdownInput/plugins/markdownDetection';
import { DEFAULT_MENTION_STATE, Mentionable, withMentions } from '@src/components/sco/RichMarkdownInput/plugins/mentions';
import { MentionSelector, MentionState } from '@src/components/sco/RichMarkdownInput/plugins/mentions/MentionSelector';
import { withShortcuts } from '@src/components/sco/RichMarkdownInput/plugins/shortcuts';
import { setGlobalOpenLinkModal } from '@src/components/sco/RichMarkdownInput/utilities/blocks';
import { deserializeNodesFromMarkdown, serializeNodesToMarkdown } from '@src/components/sco/RichMarkdownInput/utilities/markdown';
import { PrettyPrintJSON } from '@src/components/sco/utilities/PrettyPrintJSON';
import React, { Dispatch, ReactNode, SetStateAction, useEffect, useRef, useState } from 'react';
import { BaseElement, createEditor, Editor, Transforms } from 'slate';
import { withHistory } from 'slate-history';
import { Editable, ReactEditor, Slate, useSlate, withReact } from 'slate-react';
export type RichMarkdownInputValues = {
  markdown?: string;
  slate?: SlateNode[];
};
export type SlateEditor = ReactEditor & {
  onKeyDown: (editor: SlateEditor, event: KeyboardEvent) => void;
  insertMention?: (editor: SlateEditor, mentionable: Mentionable) => void;
  insertLink?: (url: string, label?: string) => void;
  mentionState?: MentionState;
  setMentionState?: Dispatch<SetStateAction<MentionState>>;
};
export type SlateNode = BaseElement & {
  type?: string;
  text?: string;
  children?: SlateNode[];
};
export function createSlateEditor(props: any = {}): SlateEditor {
  let editor: any = createEditor() as ReactEditor;
  editor = withHistory(editor);
  editor = withReact(editor);
  editor = withMarkHotKeys(editor, props.pluginConfigs?.hotKeys);
  editor = withMarkdownDetection(editor, props.pluginConfigs?.markdown);
  editor = withLinks(editor, props.pluginConfigs?.links);
  editor = withLists(editor, props.pluginConfigs?.lists);
  editor = withMentions(editor, props.pluginConfigs?.mentions);
  editor = withEmojis(editor, props.pluginConfigs?.emojis);
  editor = withImages(editor, props.pluginConfigs?.images);
  editor = withGiphy(editor, props.pluginConfigs?.giphy);
  editor = withShortcuts(editor, props.pluginConfigs?.shortcuts);
  return editor;
}

// A fresh, empty document. Always create a NEW object graph (never reuse a
// shared constant) — see `normalizeSlateValue` for why.
const createDefaultValue = (): SlateNode[] => [{
  type: 'paragraph',
  children: [{
    text: ''
  }]
}];

// Read-only reference used only for serializing the empty document to markdown.
// Never hand this to a Slate editor as its value.
const defaultInitialValue: SlateNode[] = createDefaultValue();

// Slate nodes are plain JSON-serializable data, so a structured round-trip is a
// safe, dependency-free deep clone.
const cloneSlateValue = (value: SlateNode[]): SlateNode[] => JSON.parse(JSON.stringify(value));

/**
 * Slate requires the editor's top-level value to be a non-empty array of
 * element nodes (each with a `children` array). When the incoming value is
 * empty, null-ish, or contains bare text nodes at the top level, Slate throws
 * `Unable to find the path for Slate node: {"text":""}` on mount. Normalize
 * defensively so the editor always receives a valid document.
 *
 * Critically, the returned value is always a fresh, deep-cloned object graph.
 * slate-react tracks nodes in WeakMaps keyed by object identity
 * (`NODE_TO_PARENT` / `NODE_TO_INDEX`). If two editor instances were seeded
 * with the same node objects (e.g. a shared default value), the second editor
 * to mount would clobber those map entries, and the first editor's
 * `findPath()` would walk into the wrong tree and throw
 * `Unable to find the path for Slate node: {"text":""}`. Cloning guarantees
 * every editor owns its nodes.
 */
export const normalizeSlateValue = (value: any): SlateNode[] => {
  if (!Array.isArray(value) || value.length === 0) {
    return createDefaultValue();
  }
  const normalized = value.filter((node: any) => node && typeof node === 'object').map((node: any) => {
    // A valid top-level node is an element with a `children` array.
    if (Array.isArray(node.children)) {
      return node;
    }
    // Bare text nodes at the top level are invalid — wrap them in a paragraph.
    if (typeof node.text === 'string') {
      return {
        type: 'paragraph',
        children: [node]
      };
    }
    // Element missing its children — give it an empty text node.
    return {
      ...node,
      type: node.type || 'paragraph',
      children: [{
        text: ''
      }]
    };
  });

  // Deep-clone so the editor never shares node identities with another editor
  // or with the caller's source array.
  return normalized.length ? cloneSlateValue(normalized) : createDefaultValue();
};
const defaultTypographyStyles = typographyStyles(presets.ugc.typographyStyles);

/** Cap the self-growing editor. Viewport units are resolved to px on resize. */
export const DEFAULT_EDITOR_MAX_HEIGHT = '60vh';
const VIEWPORT_HEIGHT_UNIT = /^(\d+(?:\.\d+)?)(dvh|svh|lvh|vh)$/i;
const resolveMaxHeight = (maxHeight: string, viewportHeight: number) => {
  const match = String(maxHeight).trim().match(VIEWPORT_HEIGHT_UNIT);
  if (!match) {
    return maxHeight;
  }
  return `${Math.round(parseFloat(match[1]) / 100 * viewportHeight)}px`;
};
const useResolvedMaxHeight = maxHeight => {
  const $ = _c(3);
  const [resolved, setResolved] = useState(maxHeight);
  let t0;
  let t1;
  if ($[0] !== maxHeight) {
    t0 = () => {
      const update = () => {
        const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
        setResolved(resolveMaxHeight(maxHeight, viewportHeight));
      };
      update();
      window.addEventListener("resize", update);
      window.visualViewport?.addEventListener("resize", update);
      return () => {
        window.removeEventListener("resize", update);
        window.visualViewport?.removeEventListener("resize", update);
      };
    };
    t1 = [maxHeight];
    $[0] = maxHeight;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  return resolved;
};
export type RichMarkdownInputProps = {
  disabled?: boolean;
  autoFocus?: boolean;
  boxProps?: BoxProps;
  slateBoxProps?: BoxProps;
  initialMarkdownValue?: any;
  initialSlateValue?: any;
  onBlur?: (event: any) => void;
  onChange?: (values: RichMarkdownInputValues) => void;
  onFocus?: (event: any) => void;
  onKeyDown?: (event: any) => void;
  placeholder?: any;
  printDebug?: boolean;
  minHeight?: string;
  /** Max height of the self-growing editor. Defaults to 60vh (resolved to px). Pass `none` to disable. */
  maxHeight?: string;
  additionalToolbarButtons?: ReactNode[] | ReactNode;
  toolbarPlacement?: 'top' | 'bottom';
  typographyStyles?: any;
  /** When true, shows Attach file next to Link on the formatting toolbar */
  enableFileAttachments?: boolean;
  fileAttachmentsDisabled?: boolean;
  /** Called when the user picks a file from the Attach toolbar control */
  onFileAttachmentSelected?: (file: File) => void | Promise<void>;
  pluginConfigs?: {
    emojis?: any;
    giphy?: any;
    hotKeys?: any;
    images?: any;
    links?: any;
    lists?: any;
    markdown?: any;
    mentions?: any;
    shortcuts?: any;
  };
};
export const RichMarkdownInput = (props: RichMarkdownInputProps) => {
  /**
   * Create Slate Editor instance with all plugins configured
   */
  const [editor] = useState(() => createSlateEditor(props));
  const lastSelection = useRef(editor.selection);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const resolvedMaxHeight = useResolvedMaxHeight(props.maxHeight ?? DEFAULT_EDITOR_MAX_HEIGHT);
  const openFileAttachmentPicker = () => {
    if (props.fileAttachmentsDisabled) {
      return;
    }
    fileInputRef.current?.click();
  };
  const onFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (!file) {
      return;
    }
    await props.onFileAttachmentSelected?.(file);
  };

  /**
   * Slate Value in State
   */
  const [slateValue, setSlateValue] = useState<SlateNode[]>(() => normalizeSlateValue(props.initialSlateValue ?? (props.initialMarkdownValue ? deserializeNodesFromMarkdown(props.initialMarkdownValue) : undefined)));

  /**
   * Markdown Value in State
   */
  const [markdownValue, setMarkdownValue] = useState(props.initialMarkdownValue || serializeNodesToMarkdown(defaultInitialValue));

  /**
   * Focus State
   */
  const [isFocused, setIsFocused] = useState(false);

  /**
   * Mention State
   */
  const [mentionState, setMentionState] = useState<MentionState>(DEFAULT_MENTION_STATE);
  editor.mentionState = mentionState;
  editor.setMentionState = setMentionState;

  /**
   * Call props.onChange when internal value changes
   */
  useEffect(() => {
    if (props.onChange) {
      props.onChange({
        slate: slateValue,
        markdown: markdownValue
      });
    }
  }, [markdownValue]);

  /**
   * Slate Editor Event Handlers
   */
  const handlers = {
    onChange: (newSlateValue: any) => {
      // console.log({ newSlateValue })
      if (newSlateValue?.mentionState) {
        setMentionState(newSlateValue.mentionState);
      } else {
        setSlateValue(newSlateValue);
        setMarkdownValue(serializeNodesToMarkdown(newSlateValue));
      }
    },
    onFocus: event => {
      setIsFocused(true);
      ReactEditor.focus(editor);
      if (!editor.selection) {
        Transforms.select(editor, lastSelection.current ?? Editor.end(editor, []));
      }
      if (props.onFocus) {
        props.onFocus(event);
      }
    },
    onBlur: event_0 => {
      setIsFocused(false);
      ReactEditor.blur(editor);
      if (props.onBlur) {
        props.onBlur(event_0);
      }
    },
    onKeyDown: event_1 => {
      try {
        if (editor.onKeyDown) {
          editor.onKeyDown(editor, event_1);
        }
        if (props.onKeyDown) {
          props.onKeyDown(event_1);
        }
      } catch (err) {
        console.log({
          err
        });
      }
    }
  };

  /**
   * Let's do this!
   */
  return <LinkModalProvider>
      <Wrapper boxProps={props.boxProps} isFocused={isFocused} typographyStyles={props.typographyStyles || defaultTypographyStyles}>
        <Slate editor={editor} initialValue={slateValue} onChange={handlers.onChange}>
          {props.toolbarPlacement === 'top' && <>
              <Toolbar additionalButtons={props.additionalToolbarButtons} enableFileAttachments={props.enableFileAttachments} fileAttachmentsDisabled={props.fileAttachmentsDisabled} placement={props.toolbarPlacement} onAttachFileClick={openFileAttachmentPicker} />
            </>}
          <Box fontFamily={'longform'} padding={0} onClick={event_2 => ReactEditor.focus(editor)} {...props.slateBoxProps}>
            <Editable autoFocus={props.autoFocus} placeholder={props.placeholder} readOnly={props.disabled || false} renderElement={Element} renderLeaf={Leaf} renderPlaceholder={props_0 => <Placeholder {...props_0} />} style={{
            minHeight: props.minHeight,
            maxHeight: resolvedMaxHeight,
            resize: 'vertical',
            outline: 'none',
            padding: '1em',
            overflow: 'auto'
          }} onBlur={handlers.onBlur} onFocus={handlers.onFocus} onKeyDown={handlers.onKeyDown} />
          </Box>

          {props.toolbarPlacement !== 'top' && <>
              <Toolbar additionalButtons={props.additionalToolbarButtons} enableFileAttachments={props.enableFileAttachments} fileAttachmentsDisabled={props.fileAttachmentsDisabled} placement={props.toolbarPlacement} onAttachFileClick={openFileAttachmentPicker} />
            </>}

          {mentionState.target && <MentionSelector mentionState={mentionState} setMentionState={setMentionState} />}
        </Slate>

        {props.enableFileAttachments && <input ref={fileInputRef} style={{
        display: 'none'
      }} type={'file'} onChange={onFileInputChange} />}

        {props.printDebug && <>
            <Separator />
            <PrettyPrintJSON data={{
          mentionState,
          slateValue,
          markdownValue
        }} />
          </>}

        <LinkModalWithContext />
      </Wrapper>
    </LinkModalProvider>;
};
const LinkModalWithContext = () => {
  const $ = _c(11);
  const {
    isOpen,
    initialUrl,
    initialLabel,
    title,
    onSubmit,
    onRemove,
    closeLinkModal,
    openLinkModal
  } = useLinkModal();
  let t0;
  let t1;
  if ($[0] !== openLinkModal) {
    t0 = () => {
      setGlobalOpenLinkModal(openLinkModal);
      return _temp;
    };
    t1 = [openLinkModal];
    $[0] = openLinkModal;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  if ($[3] !== closeLinkModal || $[4] !== initialLabel || $[5] !== initialUrl || $[6] !== isOpen || $[7] !== onRemove || $[8] !== onSubmit || $[9] !== title) {
    t2 = <LinkModal initialLabel={initialLabel} initialUrl={initialUrl} isOpen={isOpen} title={title} onClose={closeLinkModal} onRemove={onRemove} onSubmit={onSubmit} />;
    $[3] = closeLinkModal;
    $[4] = initialLabel;
    $[5] = initialUrl;
    $[6] = isOpen;
    $[7] = onRemove;
    $[8] = onSubmit;
    $[9] = title;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
};
const Placeholder = props => {
  const $ = _c(8);
  const editor = useSlate() as ReactEditor;
  if (editor.selection) {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <></>;
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    return t0;
  }
  let t0;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      opacity: 0.5,
      position: "absolute",
      display: "inline-block",
      marginTop: "-24px"
    };
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== editor) {
    t1 = event => {
      event.preventDefault();
      ReactEditor.focus(editor);
    };
    $[2] = editor;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== props.attributes || $[5] !== props.children || $[6] !== t1) {
    t2 = <chakra.span color="colorPalette.fg/50" {...props.attributes} style={t0} onMouseDown={t1}>{props.children}</chakra.span>;
    $[4] = props.attributes;
    $[5] = props.children;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
function _temp() {
  setGlobalOpenLinkModal(null);
}